import React, { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { AIChatMessage, AIChatMode, AIChatStep } from '../ai-chat/types';
import { AIChatMessageList } from '../ai-chat/AIChatMessageList';
import { AIChatInput } from '../ai-chat/AIChatInput';
import { AIChatModeToggle } from '../ai-chat/AIChatModeToggle';
import { ModeToggleWrapper } from '../ai-chat/AIChat.Styled';
import {
	AccordionCard,
	AccordionHeader,
	AccordionHeaderLeft,
	AccordionIconContainer,
	AccordionSparkleIcon,
	AccordionTitleGroup,
	AccordionTitle,
	AccordionSubtitle,
	AccordionHeaderRight,
	AccordionChevronButton,
	AccordionContentWrapper,
	AccordionContentInner,
	AccordionMessageListWrapper,
} from './AIChatPillAccordion.Styled';
import { Dialog } from '../dialog';
import { Tooltip } from '../tooltip';

/**
 * Copy for the clear-history flow — pass translated strings from the host app.
 * `title` is one string doing three jobs: tooltip, aria-label and dialog title.
 * ponytail: split it only if a host needs the tooltip to differ from the dialog title.
 */
export type AIChatClearHistoryLabels = {
	title?: string;
	message?: string;
	confirmLabel?: string;
	cancelLabel?: string;
};

const DEFAULT_CLEAR_HISTORY_LABELS: Required<AIChatClearHistoryLabels> = {
	title: 'Clear chat history',
	message: 'This deletes every message in this chat. This cannot be undone.',
	confirmLabel: 'Clear',
	cancelLabel: 'Cancel',
};

export type AIChatPillAccordionProps = {
	title: string;
	subtitle?: string;
	messages: AIChatMessage[];
	isSending?: boolean;
	/** What the assistant is doing while `isSending`; shown in place of the loading phrase. */
	steps?: AIChatStep[];
	onSend: (value: string) => void;
	placeholder?: string;
	assistantAvatarSrc?: string;
	assistantAvatarAlt?: string;
	autoScroll?: boolean;
	emptyState?: ReactNode;
	chatHeight?: string | number;
	modes?: AIChatMode[];
	activeModeId?: string;
	onModeChange?: (modeId: string) => void;
	defaultOpen?: boolean;
	/**
	 * Called after the user confirms deletion. The header button is hidden while the chat is
	 * empty and disabled while `isSending`.
	 */
	onClearHistory: () => void;
	clearHistoryLabels?: AIChatClearHistoryLabels;
};

export function AIChatPillAccordion({
	title,
	subtitle,
	messages,
	isSending,
	steps,
	onSend,
	placeholder,
	assistantAvatarSrc,
	assistantAvatarAlt,
	autoScroll = true,
	emptyState,
	chatHeight = 300,
	modes,
	activeModeId,
	onModeChange,
	defaultOpen = false,
	onClearHistory,
	clearHistoryLabels,
}: AIChatPillAccordionProps): React.ReactElement {
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const [inputValue, setInputValue] = useState('');
	const [isConfirmingClear, setIsConfirmingClear] = useState(false);
	const messageListRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		const log = messageListRef.current?.querySelector('[role="log"]') as HTMLElement | null;
		log?.scrollTo({ top: log.scrollHeight, behavior: 'smooth' });
	};

	const showModeToggle = modes && modes.length > 1;
	const isEmpty = messages.length === 0;
	// Derived, not synced: an empty chat is always collapsed, so clearing history
	// snaps the card back to its compact state without an effect to keep in step.
	const isExpanded = isOpen && !isEmpty;
	const clearLabels = { ...DEFAULT_CLEAR_HISTORY_LABELS, ...clearHistoryLabels };

	// Messages can arrive long after mount — the host may load history only once the chat is
	// opened — so expand on the empty→filled flip rather than trusting `defaultOpen`. Keyed on
	// `isEmpty`, not `messages.length`, so a manual collapse survives later messages.
	useEffect(() => {
		if (!isEmpty) setIsOpen(true);
	}, [isEmpty]);

	const SCROLL_TIMEOUT = 360; // Match with CSS animation duration
	// Scroll after accordion opens (wait for CSS animation)
	useEffect(() => {
		if (!isExpanded) return;
		const timer = setTimeout(scrollToBottom, SCROLL_TIMEOUT);
		return () => clearTimeout(timer);
	}, [isExpanded]);

	// Scroll on new message when already open
	useEffect(() => {
		if (!isExpanded) return;
		scrollToBottom();
	}, [messages.length, isExpanded]);

	const handleSend = (value: string) => {
		onSend(value);
		setIsOpen(true);
	};

	const confirmClear = () => {
		setIsConfirmingClear(false);
		onClearHistory();
	};

	return (
		<AccordionCard>
			<AccordionHeader>
				<AccordionHeaderLeft>
					<AccordionIconContainer>
						<AccordionSparkleIcon icon="sparkles-regular" aria-hidden="true" />
					</AccordionIconContainer>
					<AccordionTitleGroup>
						<AccordionTitle>{title}</AccordionTitle>
						{subtitle && <AccordionSubtitle>{subtitle}</AccordionSubtitle>}
					</AccordionTitleGroup>
				</AccordionHeaderLeft>
				{/* Nothing to clear or expand on an empty chat, so both controls stay out of the header */}
				{!isEmpty && (
					<AccordionHeaderRight>
						{/* Tooltip renders nothing without a message — no hint on the disabled button */}
						<Tooltip message={isSending ? undefined : clearLabels.title}>
							<AccordionChevronButton
								variant="ghost"
								size="small"
								icon="trash"
								aria-label={clearLabels.title}
								disabled={isSending}
								onClick={() => setIsConfirmingClear(true)}
							/>
						</Tooltip>
						<AccordionChevronButton
							variant="ghost"
							size="small"
							icon={isExpanded ? 'chevron-up' : 'chevron-down'}
							aria-label={isExpanded ? 'Collapse chat' : 'Expand chat'}
							onClick={() => setIsOpen(v => !v)}
						/>
					</AccordionHeaderRight>
				)}
			</AccordionHeader>

			<Dialog
				open={isConfirmingClear}
				title={clearLabels.title}
				onClose={() => setIsConfirmingClear(false)}
			>
				<Dialog.Message>{clearLabels.message}</Dialog.Message>
				<Dialog.Actions>
					<Dialog.Action
						variant="secondary"
						label={clearLabels.cancelLabel}
						onClick={() => setIsConfirmingClear(false)}
					/>
					{/* Sending can start after the dialog is open — keep the confirm locked too */}
					<Dialog.Action
						variant="danger"
						label={clearLabels.confirmLabel}
						disabled={isSending}
						onClick={confirmClear}
					/>
				</Dialog.Actions>
			</Dialog>

			{/* ponytail: aria-hidden keeps collapsed content out of the a11y tree; swap for `inert`
			    once we can drop the grid-template-rows animation or browser support lands */}
			<AccordionContentWrapper $isOpen={isExpanded} aria-hidden={!isExpanded}>
				<AccordionContentInner>
					{showModeToggle && (
						<ModeToggleWrapper>
							<AIChatModeToggle
								modes={modes}
								activeModeId={activeModeId}
								onModeChange={onModeChange}
							/>
						</ModeToggleWrapper>
					)}
					<AccordionMessageListWrapper ref={messageListRef} $height={chatHeight}>
						<AIChatMessageList
							messages={messages}
							isLoading={isSending}
							showWaveIndicator={isSending}
							steps={steps}
							assistantAvatarSrc={assistantAvatarSrc}
							assistantAvatarAlt={assistantAvatarAlt}
							autoScroll={autoScroll}
							emptyState={emptyState}
						/>
					</AccordionMessageListWrapper>
				</AccordionContentInner>
			</AccordionContentWrapper>

			<AIChatInput
				value={inputValue}
				onChange={setInputValue}
				onSend={handleSend}
				placeholder={placeholder}
				isSending={isSending}
			/>
		</AccordionCard>
	);
}
